import React from 'react';
import PropTypes from 'prop-types';
import ButtonContent from './ButtonContent/ButtonContent';
import style from './Button.style';
import ButtonBase from '../ButtonBase/ButtonBase';

const types = ['primary', 'secondary', 'text'];

const Button = React.forwardRef(
  (
    {
      children,
      type,
      size,
      iconLeft,
      iconRight,
      loading,
      disabled,
      className,
      onClick,
      onBlur,
      onFocus,
      onMouseLeave,
      onMouseOver,
      onTouchEnd,
      onTouchStart,
      id,
      href,
      'data-testid': dataTestId,
      'aria-label': dataLabel,
      'aria-describedby': ariaDescribedBy,
    },
    ref
  ) => (
    <ButtonBase
      css={[style.container.bySize[size]]}
      context="button"
      disabled={disabled}
      className={className}
      onClick={onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}
      type={types.includes(type) ? type : 'secondary'}
      id={id}
      href={href}
      data-testid={dataTestId}
      aria-label={dataLabel}
      aria-describedby={ariaDescribedBy}
      ref={ref}
    >
      <ButtonContent
        loading={loading}
        IconLeft={iconLeft}
        IconRight={iconRight}
        size={size}
      >
        {children}
      </ButtonContent>
    </ButtonBase>
  )
);

Button.defaultProps = {
  type: 'secondary',
  size: 'medium',
  loading: false,
  disabled: false,
  iconLeft: undefined,
  iconRight: undefined,
  className: undefined,
  onClick: undefined,
  onBlur: () => {},
  onFocus: () => {},
  onMouseLeave: () => {},
  onMouseOver: () => {},
  onTouchEnd: () => {},
  onTouchStart: () => {},
  id: null,
  href: null,
  'data-testid': '',
  'aria-label': '',
  'aria-describedby': undefined,
};

Button.propTypes = {
  /** The main style of the button */
  type: PropTypes.oneOf(types),

  /** The button size */
  size: PropTypes.oneOf(['small', 'medium']),

  /** Is the loading component displayed */
  loading: PropTypes.bool,

  /** Is the button disabled */
  disabled: PropTypes.bool,

  /** The icon to be displayed on the left side */
  iconLeft: PropTypes.elementType,

  /** The icon to be displayed on the right side */
  iconRight: PropTypes.elementType,

  /** The content of the button */
  children: PropTypes.node.isRequired,

  /** Custom class name to include own styles */
  className: PropTypes.string,

  /** Callback to be called on click */
  onClick: PropTypes.func,

  /** On blur callback */
  onBlur: PropTypes.func,

  /** On focus callback */
  onFocus: PropTypes.func,

  /** On mouse leave callback */
  onMouseLeave: PropTypes.func,

  /** On mouse over callback */
  onMouseOver: PropTypes.func,

  /** On touch end callback */
  onTouchEnd: PropTypes.func,

  /** On touch start callback */
  onTouchStart: PropTypes.func,

  /** Unique ID */
  id: PropTypes.string,

  /** URL for anchor button */
  href: PropTypes.string,

  'data-testid': PropTypes.string,
  'aria-label': PropTypes.string,

  /** Button aria-describedby */
  'aria-describedby': PropTypes.string,
};

Button.displayName = 'Button';

export default React.memo(Button);
